import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AndroidRoundedIcon from "@mui/icons-material/AndroidRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import AppIcon from "../components/AppIcon";
import RatingStars from "../components/RatingStars";
import {
  downloadApplication,
  fetchApplication,
} from "../services/appStoreApi";
import { withAppVisuals } from "../content/appVisuals";
import "../styles/universeAppStore.css";

const prettyDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
};

export default function ApplicationDetailsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const load = async () => {
    setLoading(true);
    setPageError("");

    try {
      const result = await fetchApplication(slug);
      setApp(withAppVisuals(result));

    } catch (error) {
      setPageError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const screenshots = useMemo(
    () => (Array.isArray(app?.screenshots) ? app.screenshots : []),
    [app]
  );

  const beginDownload = async () => {
    setDownloadLoading(true);
    setDownloadError("");

    try {
      await downloadApplication(slug, app.category);
    } catch (error) {
      setDownloadError(error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="uas-shell">
        <div className="uas-state">Loading application…</div>
      </main>
    );
  }

  if (pageError) {
    return (
      <main className="uas-shell">
        <div className="uas-state uas-state--error">
          {pageError}
          <button type="button" onClick={() => navigate("/store/universe-mobile/application")}>
            Return to applications
          </button>
        </div>
      </main>
    );
  }

  if (!app) return <main className="uas-shell"><div className="uas-state">Application unavailable.</div></main>;

  return (
    <main className="uas-shell uas-detail-shell">
      <button
        type="button"
        className="uas-back-button"
        onClick={() => navigate("/store/universe-mobile/application")}
      >
        <ArrowBackRoundedIcon />
        Applications
      </button>

      <section className="uas-detail-hero">
        <div className="uas-detail-hero__main">
          <AppIcon app={app} large />

          <div className="uas-detail-hero__copy">
            <div className="uas-title-badges">
              {app.category === "STAFF" && (
                <span className="uas-restricted-pill">
                  <LockOutlinedIcon fontSize="inherit" />
                  Staff restricted
                </span>
              )}
              <span className="uas-soft-pill">
                <AndroidRoundedIcon fontSize="inherit" />
                Android
              </span>
            </div>

            <h1>{app.name}</h1>
            <p className="uas-developer">{app.developer || "Universe Technologies"}</p>
            <p className="uas-detail-summary">{app.shortDescription}</p>
            {app.marketingBody && <p className="uas-detail-supporting-copy">{app.marketingBody}</p>}

            <div className="uas-detail-actions">
              <button
                type="button"
                className="uas-primary-button"
                onClick={beginDownload}
                disabled={downloadLoading || !app.latestRelease}
              >
                <DownloadRoundedIcon />
                {!app.latestRelease ? "Coming soon" : downloadLoading ? "Preparing download…" : `Download APK · v${app.latestRelease.version}`}
              </button>

              <span className="uas-download-note">
                {app.latestRelease ? `APK · ${app.latestRelease.sizeLabel}` : "APK is not published yet"}
              </span>
            </div>

            {downloadError && (
              <div className="uas-form-error uas-download-error">{downloadError}</div>
            )}
          </div>
        </div>

        {app.featuredImage && (
          <div className="uas-detail-hero__visual">
            <img src={app.featuredImage} alt={`${app.name} promotional artwork`} />
          </div>
        )}

        <div className="uas-metrics">
          <div>
            <strong>{app.rating?.average || "—"}</strong>
            <RatingStars value={app.rating?.average || 0} size="small" />
            <span>{app.rating?.count || 0} reviews</span>
          </div>
          <div>
            <strong>{app.latestRelease?.version || "Coming soon"}</strong>
            <span>Current version</span>
          </div>
          <div>
            <strong>{app.latestRelease?.minimumAndroid ? `${app.latestRelease.minimumAndroid}+` : "—"}</strong>
            <span>Android</span>
          </div>
          <div>
            <SecurityOutlinedIcon />
            <span>{app.latestRelease ? "Official Universe release" : "Awaiting release"}</span>
          </div>
        </div>
      </section>

      {screenshots.length > 0 ? (
        <section className="uas-detail-section">
          <div className="uas-section-heading">
            <div>
              <p className="uas-eyebrow">Preview</p>
              <h2>App screenshots</h2>
            </div>
          </div>

          <div className="uas-screenshot-strip">
            {screenshots.map((src, index) => (
              <img
                key={`${src}-${index}`}
                src={src}
                alt={`${app.name} screenshot ${index + 1}`}
              />
            ))}
          </div>
        </section>
      ) : (
        <section className="uas-preview-placeholder">
          <span>Application screenshots will appear here when uploaded.</span>
        </section>
      )}

      <div className="uas-detail-layout">
        <div className="uas-detail-layout__main">
          <section className="uas-detail-section">
            <div className="uas-section-heading">
              <div>
                <p className="uas-eyebrow">Overview</p>
                <h2>About this app</h2>
              </div>
            </div>
            <p className="uas-long-copy">{app.description}</p>
            {!!app.highlights?.length && (
              <ul className="uas-highlight-list">
                {app.highlights.map((item) => <li key={item}>{item}</li>)}
              </ul>
            )}
          </section>

          <section className="uas-detail-section">
            <div className="uas-section-heading">
              <div>
                <p className="uas-eyebrow">Release</p>
                <h2>What's new</h2>
              </div>
              <NewReleasesOutlinedIcon />
            </div>

            <div className="uas-release-card">
              <div>
                <strong>Version {app.latestRelease?.version || "Coming soon"}</strong>
                <span>{prettyDate(app.latestRelease?.releasedAt)}</span>
              </div>

              <ul>
                {(app.latestRelease?.whatsNew || ["No release notes yet."]).map(
                  (item) => (
                    <li key={item}>{item}</li>
                  )
                )}
              </ul>
            </div>
          </section>

          <section className="uas-detail-section">
            <div className="uas-section-heading">
              <div>
                <p className="uas-eyebrow">Community</p>
                <h2>Ratings & reviews</h2>
              </div>
              <div className="uas-rating-summary">
                <strong>{app.rating?.average || "0.0"}</strong>
                <RatingStars value={app.rating?.average || 0} />
              </div>
            </div>

            <div className="uas-review-list">
              {app.reviews?.length ? (
                app.reviews.map((review, index) => (
                  <article className="uas-review" key={`${review.createdAt}-${index}`}>
                    <div className="uas-review__head">
                      <div className="uas-review__avatar">
                        {(review.reviewerName || "U").slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <strong>{review.reviewerName}</strong>
                        <div>
                          <RatingStars value={review.rating} size="small" />
                          <span>{prettyDate(review.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <p>{review.comment}</p>
                  </article>
                ))
              ) : (
                <div className="uas-empty-reviews">
                  No public reviews yet. The rating will be calculated from real
                  submitted reviews.
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="uas-info-card">
          <p className="uas-eyebrow">App information</p>
          <dl>
            <div>
              <dt>Version</dt>
              <dd>{app.latestRelease?.version || "Coming soon"}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{prettyDate(app.latestRelease?.releasedAt)}</dd>
            </div>
            <div>
              <dt>Size</dt>
              <dd>{app.latestRelease?.sizeLabel || "—"}</dd>
            </div>
            <div>
              <dt>Requires Android</dt>
              <dd>{app.latestRelease?.minimumAndroid ? `${app.latestRelease.minimumAndroid}+` : "—"}</dd>
            </div>
            <div>
              <dt>Category</dt>
              <dd>{app.category === "STAFF" ? "Staff" : "Client"}</dd>
            </div>
            <div>
              <dt>Developer</dt>
              <dd>{app.developer || "Universe Technologies"}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </main>
  );
}
